import axios from "@/plugins/axios";
import { actionTypes, mutationTypes } from "./types";
import { mutationTypes as dialogMutationTypes } from "@/store/Dialog/types";
import { mutationTypes as flashMutationTypes } from "@/store/FlashMessages/types";
import { MESSAGE_STATE } from "@/store/FlashMessages/state";

// The API answers validation problems with 400 { error }, which should reach the
// admin as-is (e.g. "item_template: line 1: unexpected end of template").
const showResult = (commit, text, error) => {
    commit(flashMutationTypes.addMessageToQueue, {
        header: "",
        description: error ? `${text} failed: ${error}` : text,
        messageState: error ? MESSAGE_STATE.WARN : MESSAGE_STATE.SUCCESS,
    });
};

const errorOf = (reason) => reason?.response?.data?.error || "unexpected error";

export const actions = {
    [actionTypes.getConfigs]({ commit }) {
        commit(mutationTypes.getConfigsStart);
        return axios({ method: "get", url: "sql/alerts/configs" }).then(
            ({ data }) => commit(mutationTypes.getConfigsSuccess, data),
            () => commit(mutationTypes.getConfigsFailure)
        );
    },

    [actionTypes.updateConfig]({ commit, dispatch }, { id, ...payload }) {
        return axios({ method: "put", url: `sql/alerts/configs/${id}`, data: payload }).then(
            () => {
                showResult(commit, "Alert type saved.");
                commit(dialogMutationTypes.closeDialog);
                dispatch(actionTypes.getConfigs);
            },
            (reason) => showResult(commit, "Saving the alert type", errorOf(reason))
        );
    },

    // Resolves with { text, blocks, alert_count, variables, errors } - not stored,
    // the form keeps its own preview.
    // eslint-disable-next-line no-unused-vars
    [actionTypes.preview]({ commit }, payload) {
        return axios({ method: "post", url: "sql/alerts/preview", data: payload }).then(({ data }) => data);
    },

    // { id, dry_run } - resolves with { status, channel, data, blocks, delivery_enabled }.
    // A real send reports where it went (or why not) and refreshes stats + log.
    [actionTypes.sendTest]({ commit, dispatch }, { id, dry_run }) {
        return axios({ method: "post", url: `sql/alerts/configs/${id}/test`, data: { dry_run: !!dry_run } }).then(
            ({ data }) => {
                if (!dry_run) {
                    // The API always really sends a test (also outside production);
                    // a failure carries Slack's reason, e.g. not_in_channel
                    if (data.status === "sent") {
                        showResult(commit, `Test alert sent to ${data.channel}.`);
                    } else {
                        showResult(commit, `Sending the test alert to ${data.channel}`, data.error || data.status);
                    }
                    commit(dialogMutationTypes.closeDialog);
                    dispatch(actionTypes.getConfigs);
                    dispatch(actionTypes.getAlerts);
                }
                return data;
            },
            (reason) => {
                showResult(commit, "Sending the test alert", errorOf(reason));
                throw reason;
            }
        );
    },

    [actionTypes.getSilences]({ commit }) {
        commit(mutationTypes.getSilencesStart);
        return axios({ method: "get", url: "sql/alerts/silences" }).then(
            ({ data }) => commit(mutationTypes.getSilencesSuccess, data),
            () => commit(mutationTypes.getSilencesFailure)
        );
    },

    [actionTypes.saveSilence]({ commit, dispatch }, { id, ...payload }) {
        const request = id
            ? axios({ method: "put", url: `sql/alerts/silences/${id}`, data: payload })
            : axios({ method: "post", url: "sql/alerts/silences", data: payload });
        return request.then(
            () => {
                showResult(commit, id ? "Silence saved." : "Silence created.");
                commit(dialogMutationTypes.closeDialog);
                dispatch(actionTypes.getSilences);
            },
            (reason) => showResult(commit, "Saving the silence", errorOf(reason))
        );
    },

    [actionTypes.deleteSilence]({ commit, dispatch }, { id }) {
        return axios({ method: "delete", url: `sql/alerts/silences/${id}` }).then(
            () => {
                showResult(commit, "Silence deleted.");
                commit(dialogMutationTypes.closeDialog);
                dispatch(actionTypes.getSilences);
            },
            (reason) => showResult(commit, "Deleting the silence", errorOf(reason))
        );
    },

    [actionTypes.getAlerts]({ commit }, { code, status } = {}) {
        const params = new URLSearchParams();
        if (code) params.append("code", code);
        if (status) params.append("status", status);
        params.append("limit", "200");

        commit(mutationTypes.getAlertsStart);
        return axios({ method: "get", url: `sql/alerts?${params.toString()}` }).then(
            ({ data }) => commit(mutationTypes.getAlertsSuccess, data),
            () => commit(mutationTypes.getAlertsFailure)
        );
    },
};
